import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';
import { richSync } from '../board/text/richLoader';
import { HttpProvider } from '../sync/HttpProvider';
import {
  INK_KINDS,
  INK_LAYER_NAME,
  inkKindOf,
  inkLayerId,
  RICH_TYPES,
  type InkKind,
  type Item,
  type ItemPatch,
  type ItemType,
  type LayerDef,
  type NewItem,
  type SheetLayerDef,
  type StrokeItem,
} from './schema';
import { ItemIndex } from './ItemIndex';
import { safeKeyBetween } from './zorder';

/** Transaction origin for local user edits. Only these are tracked by the undo manager. */
export const LOCAL_ORIGIN = 'local';
/** Origin for automatic housekeeping (stroke adoption): synced like any edit, but never undoable. */
export const MIGRATE_ORIGIN = 'migrate';
/**
 * Origin for sketch-sheet pixel commits (a layer's asset changing after painting). Synced, but
 * not tracked by the board's undo manager: pixels have their own undo history while painting
 * (see board/paint/rasterStore.ts), and a board undo must never make a drawing jump back.
 */
export const PAINT_ORIGIN = 'paint';

export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * One open board: the Y.Doc, its IndexedDB persistence, undo manager, read index and,
 * for server boards, the HTTP sync provider.
 */
export class BoardDoc {
  readonly doc = new Y.Doc();
  readonly items: Y.Map<Y.Map<unknown>>;
  readonly meta: Y.Map<unknown>;
  readonly views: Y.Map<Y.Map<unknown>>;
  readonly layers: Y.Map<Y.Map<unknown>>;
  private layerCache = new Map<string, LayerDef>();
  private layerOrder: string[] = [];
  readonly undo: Y.UndoManager;
  readonly index: ItemIndex;
  readonly persistence: IndexeddbPersistence;
  readonly provider: HttpProvider | null;
  readonly whenReady: Promise<void>;

  constructor(
    readonly boardId: string,
    opts: { remote?: boolean } = {},
  ) {
    this.items = this.doc.getMap('items');
    this.meta = this.doc.getMap('meta');
    this.views = this.doc.getMap('views');
    this.layers = this.doc.getMap('layers');
    this.index = new ItemIndex(this.items);
    this.refreshLayers();
    this.layers.observeDeep(this.refreshLayers);
    this.undo = new Y.UndoManager([this.items, this.meta, this.views, this.layers], {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
      captureTimeout: 300,
    });
    // When each undo step was made, so paint undo (a separate history) can tell which is newer.
    const stamp = (e: { stackItem: { meta: Map<unknown, unknown> } }) => e.stackItem.meta.set('at', Date.now());
    this.undo.on('stack-item-added', stamp);
    this.undo.on('stack-item-updated', stamp);
    this.persistence = new IndexeddbPersistence(`vds:board:${boardId}`, this.doc);
    this.whenReady = this.persistence.whenSynced.then(() => undefined);
    this.provider = opts.remote ? new HttpProvider(this.doc, boardId) : null;
    if (this.provider) {
      const provider = this.provider;
      // Local cache first, then merge with the server and push our diff.
      void this.whenReady.then(() => provider.connect());
    }
  }

  get remote(): boolean {
    return this.provider !== null;
  }

  transact(fn: () => void): void {
    this.doc.transact(fn, LOCAL_ORIGIN);
  }

  topZ(): string | null {
    const all = this.index.all();
    const last = all[all.length - 1];
    return last ? last.z : null;
  }

  createItem(input: NewItem): string {
    return this.createItems([input])[0] ?? '';
  }

  /**
   * Creates items in one transaction. Without an explicit z, notes go on top of everything,
   * frames go underneath everything, and other items go just below the lowest note, so sticky
   * notes stay visible by default.
   * Explicit z-order commands can still override this.
   */
  createItems(inputs: NewItem[]): string[] {
    const ids: string[] = [];
    this.transact(() => {
      const all = this.index.all();
      const firstNote = all.findIndex((i) => i.type === 'note');
      const noteFloor = firstNote >= 0 ? all[firstNote]!.z : null;
      let belowNotes = firstNote > 0 ? all[firstNote - 1]!.z : null;
      let top = this.topZ();
      let bottom = all[0]?.z ?? null;
      const now = Date.now();
      for (const input of inputs) {
        const id = input.id ?? newId();
        let z: string;
        if (input.z) {
          z = input.z;
        } else if (input.type === 'frame') {
          // frames are containers: they start underneath everything
          z = safeKeyBetween(null, bottom);
          bottom = z;
          if (top === null) top = z;
        } else if (input.type === 'note' || noteFloor === null) {
          z = safeKeyBetween(top, null);
          top = z;
        } else {
          z = safeKeyBetween(belowNotes, noteFloor);
          belowNotes = z;
        }
        const { props, ...rest } = input;
        const y = new Y.Map<unknown>();
        // strokes always sit on the ink layer of their tool, whatever the input says
        const layerId =
          input.type === 'stroke'
            ? this.ensureInkLayer(inkKindOf(input.props.tool))
            : input.layerId === undefined
              ? this.autoLayerFor(input.type)
              : input.layerId;
        const fields: Record<string, unknown> = {
          rotation: 0,
          groupId: null,
          locked: false,
          createdAt: now,
          updatedAt: now,
          ...rest,
          id,
          z,
        };
        fields.layerId = layerId;
        for (const [k, v] of Object.entries(fields)) y.set(k, v);
        const p = new Y.Map<unknown>();
        const rich = RICH_TYPES.has(input.type);
        let contentJson: unknown = null;
        for (const [k, v] of Object.entries(props)) {
          if (rich && (k === 'content' || k === 'contentJson')) {
            if (k === 'contentJson') contentJson = v;
            continue; // the body is a fragment, never a plain value
          }
          if (input.type === 'sheet' && k === 'layers') {
            // sheet layers are nested maps (one per layer) so edits to different layers merge
            p.set(k, sheetLayersToY(v as Record<string, Partial<SheetLayerDef>>));
            continue;
          }
          p.set(k, v);
        }
        y.set('props', p);
        this.items.set(id, y);
        if (rich) {
          const frag = new Y.XmlFragment();
          p.set('content', frag);
          if (contentJson && typeof contentJson === 'object') {
            const rich = richSync();
            if (!rich) console.warn('[doc] rich module not loaded; content dropped (call loadRich() first)');
            else
              try {
                rich.jsonToFragment(contentJson, frag);
              } catch (e) {
                console.warn('[doc] could not restore rich content', e);
              }
          }
        }
        ids.push(id);
      }
    });
    return ids;
  }

  updateItem(id: string, patch: ItemPatch): void {
    this.transact(() => this.applyPatch(id, patch));
  }

  updateItems(patches: Iterable<[string, ItemPatch]>): void {
    this.transact(() => {
      for (const [id, patch] of patches) this.applyPatch(id, patch);
    });
  }

  private applyPatch(id: string, patch: ItemPatch): void {
    const y = this.items.get(id);
    if (!y) return;
    const { props, ...rest } = patch;
    for (const [k, v] of Object.entries(rest)) {
      if (v !== undefined) y.set(k, v);
    }
    if (props) {
      const p = y.get('props') as Y.Map<unknown> | undefined;
      if (p) {
        for (const [k, v] of Object.entries(props)) p.set(k, v);
      }
      // a stroke follows its tool: pen -> highlighter moves it between ink layers
      if (y.get('type') === 'stroke' && typeof props.tool === 'string') {
        y.set('layerId', this.ensureInkLayer(inkKindOf(props.tool)));
      }
    }
    y.set('updatedAt', Date.now());
  }

  deleteItems(ids: Iterable<string>): void {
    this.transact(() => {
      for (const id of ids) this.items.delete(id);
    });
  }

  clear(): void {
    this.deleteItems([...this.items.keys()]);
  }

  // ---- layers ---------------------------------------------------------------

  private refreshLayers = (): void => {
    const raw = this.layers.toJSON() as Record<string, Partial<LayerDef>>;
    this.layerCache = new Map();
    for (const [id, l] of Object.entries(raw)) {
      this.layerCache.set(id, {
        name: l.name ?? 'Layer',
        order: l.order ?? 0,
        visible: l.visible ?? true,
        locked: l.locked ?? false,
        autoTypes: Array.isArray(l.autoTypes) ? (l.autoTypes as ItemType[]) : [],
        ink: typeof l.ink === 'string' ? inkKindOf(l.ink) : null,
      });
    }
    this.layerOrder = [...this.layerCache.entries()].sort((a, b) => a[1].order - b[1].order).map(([id]) => id);
    const ranks = new Map<string, number>();
    this.layerOrder.forEach((id, i) => ranks.set(id, i + 1));
    this.index.setLayerRanks(ranks);
  };

  layer(id: string | null | undefined): LayerDef | undefined {
    return id ? this.layerCache.get(id) : undefined;
  }

  /** Item is on a hidden layer. */
  isHidden(item: Item): boolean {
    return this.layer(item.layerId)?.visible === false;
  }

  /** Item is locked itself or sits on a locked layer. */
  isLocked(item: Item): boolean {
    return item.locked || this.layer(item.layerId)?.locked === true;
  }

  /** Layer that auto-collects this item type, if any (lowest order wins). */
  autoLayerFor(type: ItemType): string | null {
    for (const id of this.layerOrder) {
      if (this.layerCache.get(id)!.autoTypes.includes(type)) return id;
    }
    return null;
  }

  private nextLayerOrder(): number {
    let max = 0;
    this.layers.forEach((y) => {
      const o = y.get('order');
      if (typeof o === 'number' && o > max) max = o;
    });
    return max + 1;
  }

  createLayer(name: string): string {
    const id = newId();
    const order = this.nextLayerOrder();
    this.transact(() => {
      const y = new Y.Map<unknown>();
      const def: LayerDef = { name, order, visible: true, locked: false, autoTypes: [] };
      for (const [k, v] of Object.entries(def)) y.set(k, v);
      this.layers.set(id, y);
    });
    return id;
  }

  // ---- sketch sheet layers --------------------------------------------------------

  /** The live layers map of a sheet, or null. */
  sheetLayers(itemId: string): Y.Map<Y.Map<unknown>> | null {
    const y = this.items.get(itemId);
    if (!y || y.get('type') !== 'sheet') return null;
    const p = y.get('props');
    const layers = p instanceof Y.Map ? p.get('layers') : null;
    return layers instanceof Y.Map ? (layers as Y.Map<Y.Map<unknown>>) : null;
  }

  /** Layer ids of a sheet, bottom to top. */
  sheetLayerOrder(itemId: string): string[] {
    const item = this.index.get(itemId);
    if (!item || item.type !== 'sheet') return [];
    return Object.entries(item.props.layers)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([id]) => id);
  }

  /**
   * Adds an empty layer above `aboveId` (or on top) and returns its id. `assetId` seeds it
   * (duplicate). Undoable like any structural edit.
   */
  addSheetLayer(itemId: string, opts: { name?: string; aboveId?: string | null; assetId?: string | null } = {}): string | null {
    const layers = this.sheetLayers(itemId);
    if (!layers) return null;
    const id = newId();
    this.transact(() => {
      const order = this.sheetLayerOrder(itemId);
      const count = order.length;
      const at = opts.aboveId ? order.indexOf(opts.aboveId) + 1 : count;
      // renumber so the new layer slots in: orders are small integers, cheap to rewrite
      const next = [...order.slice(0, at), id, ...order.slice(at)];
      next.forEach((lid, i) => {
        if (lid === id) return;
        layers.get(lid)?.set('order', i);
      });
      const def: SheetLayerDef = {
        name: opts.name ?? `Layer ${count + 1}`,
        order: at,
        visible: true,
        locked: false,
        opacity: 1,
        assetId: opts.assetId ?? null,
      };
      layers.set(id, sheetLayerToY(def));
    });
    return id;
  }

  /** Name, visibility, lock, opacity (and, for structural edits like clear or merge, the asset). Undoable. */
  updateSheetLayer(itemId: string, layerId: string, patch: Partial<SheetLayerDef>): void {
    const y = this.sheetLayers(itemId)?.get(layerId);
    if (!y) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) y.set(k, v);
      this.items.get(itemId)?.set('updatedAt', Date.now());
    });
  }

  /** A paint commit: the layer's pixels changed. Synced, never part of the board's undo history. */
  setSheetLayerAsset(itemId: string, layerId: string, assetId: string | null): void {
    const y = this.sheetLayers(itemId)?.get(layerId);
    if (!y) return;
    this.doc.transact(() => {
      y.set('assetId', assetId);
      this.items.get(itemId)?.set('updatedAt', Date.now());
    }, PAINT_ORIGIN);
  }

  /** Swap a layer with its neighbour above (+1) or below (-1). */
  moveSheetLayer(itemId: string, layerId: string, dir: 1 | -1): void {
    const layers = this.sheetLayers(itemId);
    if (!layers) return;
    const order = this.sheetLayerOrder(itemId);
    const i = order.indexOf(layerId);
    const other = order[i + dir];
    if (i < 0 || !other) return;
    this.transact(() => {
      layers.get(layerId)?.set('order', i + dir);
      layers.get(other)?.set('order', i);
    });
  }

  /** Removes a layer. The last layer of a sheet cannot go: it is cleared instead. */
  deleteSheetLayer(itemId: string, layerId: string): void {
    const layers = this.sheetLayers(itemId);
    if (!layers || !layers.has(layerId)) return;
    this.transact(() => {
      if (layers.size <= 1) {
        layers.get(layerId)?.set('assetId', null);
        return;
      }
      layers.delete(layerId);
      this.sheetLayerOrder(itemId)
        .filter((id) => id !== layerId)
        .forEach((id, i) => layers.get(id)?.set('order', i));
    });
  }

  /**
   * Merge down: the layer below takes the composite (already rendered by the caller as a new
   * asset) and the upper layer goes, in one undoable step.
   */
  mergeSheetLayerDown(itemId: string, layerId: string, mergedAssetId: string | null): void {
    const layers = this.sheetLayers(itemId);
    if (!layers) return;
    const order = this.sheetLayerOrder(itemId);
    const i = order.indexOf(layerId);
    const below = i > 0 ? order[i - 1] : undefined;
    if (!below) return;
    this.transact(() => {
      layers.get(below)?.set('assetId', mergedAssetId);
      layers.delete(layerId);
      order.filter((id) => id !== layerId).forEach((id, k) => layers.get(id)?.set('order', k));
      this.items.get(itemId)?.set('updatedAt', Date.now());
    });
  }

  // ---- ink layers -----------------------------------------------------------

  /**
   * The ink layer holding strokes of this kind, or null. Reads the live map (not the cache),
   * so it is correct in the middle of a transaction that just created the layer.
   */
  inkLayerFor(kind: InkKind): string | null {
    const fixed = inkLayerId(kind);
    if (this.layers.has(fixed)) return fixed;
    let found: string | null = null;
    this.layers.forEach((y, id) => {
      if (found === null && y.get('ink') === kind) found = id;
    });
    return found;
  }

  /**
   * The ink layer for a kind, created when the board has none yet: on top of the stack, except
   * that fills start underneath the other ink, so line work drawn over them stays visible.
   */
  ensureInkLayer(kind: InkKind): string {
    const existing = this.inkLayerFor(kind);
    if (existing) return existing;
    const id = inkLayerId(kind);
    const order = kind === 'fill' ? this.belowInkOrder() : this.nextLayerOrder();
    const y = new Y.Map<unknown>();
    const def: LayerDef = { name: INK_LAYER_NAME[kind], order, visible: true, locked: false, autoTypes: [], ink: kind };
    for (const [k, v] of Object.entries(def)) y.set(k, v);
    // inside a caller's transaction this joins it (Yjs ignores the origin of a nested transact)
    this.doc.transact(() => this.layers.set(id, y), LOCAL_ORIGIN);
    return id;
  }

  /** An order just under the lowest ink layer (and above whatever sits below it), or the top. */
  private belowInkOrder(): number {
    let lowest = Infinity;
    this.layers.forEach((y) => {
      const o = y.get('order');
      if (INK_KINDS.includes(y.get('ink') as InkKind) && typeof o === 'number' && o < lowest) lowest = o;
    });
    if (lowest === Infinity) return this.nextLayerOrder();
    let below = -Infinity;
    this.layers.forEach((y) => {
      const o = y.get('order');
      if (typeof o === 'number' && o < lowest && o > below) below = o;
    });
    return below === -Infinity ? lowest - 1 : (below + lowest) / 2;
  }

  /**
   * Moves every stroke that is not on the ink layer for its kind there (boards made before ink
   * layers existed, layers deleted underneath strokes). Idempotent, and not undoable.
   */
  adoptStrokes(): void {
    const stray: StrokeItem[] = [];
    const used = new Set<string>();
    for (const item of this.index.all()) {
      if (item.type === 'stroke' && item.layerId !== this.inkLayerFor(inkKindOf(item.props.tool))) stray.push(item);
      else if (item.layerId) used.add(item.layerId);
    }
    // An older client files strokes of tools it does not know on a layer per tool
    // ("ink:fineliner"): once their strokes are back with the pens, those layers go.
    const unknown: string[] = [];
    this.layers.forEach((y, id) => {
      const ink = y.get('ink');
      if (typeof ink === 'string' && !INK_KINDS.includes(ink as InkKind) && !used.has(id)) unknown.push(id);
    });
    if (!stray.length && !unknown.length) return;
    this.doc.transact(() => {
      for (const item of stray) {
        this.items.get(item.id)?.set('layerId', this.ensureInkLayer(inkKindOf(item.props.tool)));
      }
      for (const id of unknown) this.layers.delete(id);
    }, MIGRATE_ORIGIN);
  }

  updateLayer(id: string, patch: Partial<LayerDef>): void {
    const y = this.layers.get(id);
    if (!y) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) y.set(k, v);
    });
  }

  /** Swap order with the neighbour above (+1) or below (-1). */
  moveLayer(id: string, dir: 1 | -1): void {
    const i = this.layerOrder.indexOf(id);
    const j = i + dir;
    const other = this.layerOrder[j];
    if (i < 0 || !other) return;
    const a = this.layerCache.get(id)!.order;
    const b = this.layerCache.get(other)!.order;
    this.transact(() => {
      this.layers.get(id)?.set('order', b);
      this.layers.get(other)?.set('order', a);
    });
  }

  /** Deletes a layer; its items move to Default (strokes back to their ink layer). Ink layers must be empty. */
  deleteLayer(id: string): void {
    this.transact(() => {
      this.layers.delete(id);
      for (const item of this.index.all()) {
        if (item.layerId !== id) continue;
        this.items.get(item.id)?.set('layerId', item.type === 'stroke' ? this.ensureInkLayer(inkKindOf(item.props.tool)) : null);
      }
    });
  }

  /** Give a type to one layer only. */
  toggleAutoType(id: string, type: ItemType): void {
    const cur = this.layerCache.get(id);
    if (!cur) return;
    const has = cur.autoTypes.includes(type);
    this.transact(() => {
      if (!has) {
        for (const [otherId, other] of this.layerCache) {
          if (otherId !== id && other.autoTypes.includes(type)) {
            this.layers.get(otherId)?.set('autoTypes', other.autoTypes.filter((t) => t !== type));
          }
        }
      }
      this.layers.get(id)?.set('autoTypes', has ? cur.autoTypes.filter((t) => t !== type) : [...cur.autoTypes, type]);
    });
  }

  /** Move every existing item of the layer's auto types onto it. */
  applyAutoLayer(id: string): void {
    const def = this.layerCache.get(id);
    if (!def) return;
    const patches: [string, ItemPatch][] = [];
    for (const item of this.index.all()) {
      if (def.autoTypes.includes(item.type) && item.layerId !== id) patches.push([item.id, { layerId: id }]);
    }
    if (patches.length) this.updateItems(patches);
  }

  /** Moves items to a layer. Strokes are skipped: they always stay on their tool's ink layer. */
  moveSelectionToLayer(layerId: string | null, ids: string[]): void {
    const movable = ids.filter((id) => this.index.get(id)?.type !== 'stroke');
    if (movable.length) this.updateItems(movable.map((id): [string, ItemPatch] => [id, { layerId }]));
  }

  /** Merge keys into the board's meta map (background, grid, ...). */
  setMeta(patch: Record<string, unknown>): void {
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === undefined) this.meta.delete(k);
        else this.meta.set(k, v);
      }
    });
  }

  saveView(view: Record<string, unknown>): string {
    const id = newId();
    this.transact(() => {
      const y = new Y.Map<unknown>();
      for (const [k, v] of Object.entries(view)) y.set(k, v);
      this.views.set(id, y);
    });
    return id;
  }

  updateView(id: string, patch: Record<string, unknown>): void {
    const y = this.views.get(id);
    if (!y) return;
    this.transact(() => {
      for (const [k, v] of Object.entries(patch)) y.set(k, v);
    });
  }

  deleteView(id: string): void {
    this.transact(() => this.views.delete(id));
  }

  /** Live rich-text body of a richtext item (shared with the editor), or null. */
  getFragment(id: string): Y.XmlFragment | null {
    const y = this.items.get(id);
    const p = y?.get('props');
    const f = p instanceof Y.Map ? p.get('content') : null;
    return f instanceof Y.XmlFragment ? f : null;
  }

  /** Plain snapshot, e.g. for the clipboard or JSON export. Rich bodies travel as ProseMirror JSON. */
  snapshotItems(ids: Iterable<string>): Item[] {
    const out: Item[] = [];
    for (const id of ids) {
      const item = this.index.get(id);
      if (!item) continue;
      const copy = structuredClone(item);
      if (RICH_TYPES.has(copy.type)) {
        const frag = this.getFragment(id);
        const props = copy.props as { content?: string; contentJson?: unknown };
        delete props.content;
        const rich = richSync();
        if (frag && rich) props.contentJson = rich.fragmentToJson(frag);
      }
      out.push(copy);
    }
    return out;
  }

  destroy(): void {
    this.layers.unobserveDeep(this.refreshLayers);
    this.provider?.destroy();
    this.undo.destroy();
    this.index.destroy();
    void this.persistence.destroy();
    this.doc.destroy();
  }
}

function sheetLayerToY(def: SheetLayerDef): Y.Map<unknown> {
  const y = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(def)) y.set(k, v);
  return y;
}

/** Plain layer objects (a pasted or duplicated sheet, a backup) to the nested maps the document keeps. */
function sheetLayersToY(plain: Record<string, Partial<SheetLayerDef>>): Y.Map<Y.Map<unknown>> {
  const out = new Y.Map<Y.Map<unknown>>();
  const entries = Object.entries(plain ?? {}).sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0));
  if (!entries.length) entries.push([newId(), {}]);
  entries.forEach(([id, l], i) => {
    out.set(id, sheetLayerToY({
      name: typeof l.name === 'string' ? l.name : `Layer ${i + 1}`,
      order: i,
      visible: l.visible !== false,
      locked: l.locked === true,
      opacity: typeof l.opacity === 'number' ? Math.min(1, Math.max(0, l.opacity)) : 1,
      assetId: typeof l.assetId === 'string' ? l.assetId : null,
    }));
  });
  return out;
}
